import React, { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import BaseModal from '../utils/BaseModal/BaseModal';
import { notifyError } from '../../../utils/notify';
import { useDispatch, useSelector } from 'react-redux';
import { createMassPayUser, fetchMassPayCountries } from '../../../store/slices/withdrawal/asyncThunks';
import { selectMassPayCountries, selectWithdrawalActionsPending } from '../../../store/slices/withdrawal/slice';
import { ButtonWrapper, Form, NoteText, SubmitButton } from './CreateMassPayUserModal.styles';
import Input from '../../utils/inputs/Input/Input';
import SelectAlt from '../../utils/SelectAlt/SelectAlt';

const createUserFormSchema = yup.object({
  email: yup.string().trim().required('Email is required').email('Invalid email'),
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  country: yup.mixed().required('Country is required'),
});

const CreateMassPayUserModal = ({ modalRef }) => {
  const dispatch = useDispatch();
  const massPayCountries = useSelector(selectMassPayCountries);
  const { createMassPayUser: createMassPayUserPending } = useSelector(selectWithdrawalActionsPending);

  useEffect(() => {
    dispatch(fetchMassPayCountries());
  }, [dispatch]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
    setValue,
  } = useForm({
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      country: null,
    },
    delayError: 300,
    resolver: yupResolver(createUserFormSchema),
  });

  useEffect(() => {
    if (massPayCountries.length) {
      const usaCountry = massPayCountries.find(c => c.symbol === 'USA');
      setValue('country', { label: usaCountry.name, value: usaCountry.symbol });
    }
  }, [massPayCountries, setValue]);

  const onSubmit = data => {
    dispatch(createMassPayUser({ ...data, country: data.country.value }))
      .unwrap()
      .then(({ activationUrl }) => {
        reset();
        modalRef.current.hide();
        window.location.href = activationUrl;
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  const countriesOptions = massPayCountries.map(t => ({ label: t.name, value: t.symbol }));

  return (
    <BaseModal
      title="MassPay Payout Registration"
      ref={modalRef}
      onClose={() => {
        modalRef.current.hide();
      }}
      onAfterClose={() => {
        reset();
      }}>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <NoteText>
          Please provide an email address you personally have access to (personal or business), as MassPay will notify
          you when you’re able to complete your withdrawal.
        </NoteText>
        <Input
          label="Email"
          placeholder="Enter email"
          {...register('email')}
          error={errors.email?.message}
          disabled={createMassPayUserPending}
        />
        <Input
          label="Legal First Name"
          placeholder="Enter legal first name"
          {...register('firstName')}
          error={errors.firstName?.message}
          disabled={createMassPayUserPending}
          tooltipText="The name on your MassPay account must match your personal legal name (the individual who owns this account), or, if you are operating through a business, your company’s legal name, provided you are an authorized representative of that company."
        />
        <Input
          label="Legal Last Name"
          placeholder="Enter legal last name"
          {...register('lastName')}
          error={errors.lastName?.message}
          disabled={createMassPayUserPending}
          tooltipText="The name on your MassPay account must match your personal legal name (the individual who owns this account), or, if you are operating through a business, your company’s legal name, provided you are an authorized representative of that company."
        />
        <Controller
          name="country"
          control={control}
          render={({ field }) => {
            return (
              <SelectAlt
                selectedOption={field.value}
                handleChange={field.onChange}
                label="Country"
                error={errors.country?.message}
                options={countriesOptions}
              />
            );
          }}
        />
        <ButtonWrapper>
          <div />
          <SubmitButton type="submit" title="Save" isLoading={createMassPayUserPending} />
        </ButtonWrapper>
      </Form>
    </BaseModal>
  );
};

export default CreateMassPayUserModal;
